import React, { CSSProperties } from 'react'
import AgTechVirtualizedList, { AgTechVirtualizedListGroupingProps, AgTechVirtualizedListItemSettings } from '../Lists/AgTechVirtualizedList';
import { AgTechNoRecordsPageProps } from '../Pages/Error/AgTechNoRecordsPage';
import AgTechButtonGroup from '../Buttons/AgTechButtonGroup';
import AgTechSearchControl, { AgTechCoreSearchControlProps, AgTechSearchControlState } from '../Widgets/AgTechSearchControl';

export interface IAgTechListItem {
    id: string | number
}

export type AgTechListHeaderProps<TItem extends IAgTechListItem> = (items: TItem[]) => {
    title: string,
    content?: () => React.ReactNode
}

export type AgTechCoreListProps<TItem extends IAgTechListItem> = {
    header?: AgTechListHeaderProps<TItem>,
    items: TItem[],
    itemRow: (item: TItem) => React.ReactNode,
    itemRowClasses?: (item: TItem) => string,
    itemGroups?: {
        [name: string]: { filter: (item: TItem) => boolean }
    },
    itemHeight: number,
    itemSettings?: AgTechVirtualizedListItemSettings,
    listClasses?: string,
    listStyles?: CSSProperties,
    grouping?: AgTechVirtualizedListGroupingProps<TItem>,
    searchProps?: AgTechCoreSearchControlProps<TItem>,
    footer?: () => React.ReactNode,
    noRecordsConfig: AgTechNoRecordsPageProps
}

export type AgTechListProps<TItem extends IAgTechListItem> = AgTechCoreListProps<TItem> &  {
    footer?: () => React.ReactNode
};

const AgTechList = <TItem extends IAgTechListItem>(props: AgTechListProps<TItem>) => {
    const [currentGroup, setCurrentGroup] = React.useState<string | undefined>(
        props.itemGroups ? Object.keys(props.itemGroups)[0] : undefined);

    const [searchState, setSearchState] = React.useState<AgTechSearchControlState<TItem> | undefined>(undefined);

    const currentFilter = currentGroup ? props.itemGroups![currentGroup] : undefined;
    let itemsToShow = currentFilter ? props.items.filter(currentFilter.filter) : props.items;

    itemsToShow = searchState?.selectedField
        ? itemsToShow.filter(item => searchState?.selectedField.value(item)?.includes(searchState.filter))
        : itemsToShow;

    return (
        <div className={`stretched row-based overflow-hidden flex ${props.listClasses ?? ''}`} style={props.listStyles}>
            { props.header && (
                <div className='column-based-vertically-centered px-2 pt-2 mb-3 wide flex'>
                    <h1 className='text-plus ml-1 font-semibold'>
                        {props.header(itemsToShow).title}
                    </h1>
                    <div className='ml-auto column-based tall flex'>
                        {props.itemGroups && (
                            <div className='tall mr-4 flex'>
                                <AgTechButtonGroup
                                    buttons={Object.keys(props.itemGroups).map(groupName => ({
                                        text: groupName,
                                        action: () => setCurrentGroup(groupName),
                                        classes: 'font-semibold'
                                    }))}
                                />
                            </div>
                        )}
                        {props.header(itemsToShow).content?.()}
                        {props.searchProps && (
                            <AgTechSearchControl
                                {...props.searchProps}
                                onUpdateSearchState={updatedSearchState => setSearchState({
                                    ...updatedSearchState
                                })}
                            />
                        )}
                    </div>
                </div>
            )}
            <div className="flexed overflow-hidden flex">
                <AgTechVirtualizedList
                    items={itemsToShow}
                    itemRow={props.itemRow}
                    itemRowClasses={props.itemRowClasses}
                    itemHeight={props.itemHeight}
                    itemSettings={props.itemSettings}
                    grouping={props.grouping}
                    noRecordsConfig={props.noRecordsConfig}
                />
            </div>

            { props.footer ? props.footer() : null }
        </div>
    )
}

export default AgTechList;